import type { SkipTransformationResponse } from '../../types';
import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

/**
 * Skips the transformation pipeline by calling the dedicated skip-transformation endpoint.
 * Copies export files directly to the transformed folder and advances to ImportQueued.
 */
const skipTransformation = async (
  bearerToken: string,
  migrationId: string
): Promise<SkipTransformationResponse> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${migrationId}/skip-transformation`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(bearerToken),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      logger.error('Skip transformation API returned error', {
        status: response.status,
        statusText: response.statusText,
        migrationId,
        url
      });
      throw new Error(`Error skipping transformation: ${response.status} ${response.statusText}`);
    }

    const data: SkipTransformationResponse = await response.json();
    logger.info('Transformation skipped successfully', { migrationId });
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to skip transformation', {
      error: msg,
      migrationId,
      url: `${getEnv().VITE_EDCI_API_URL}/api/migrations/${migrationId}/skip-transformation`
    });
    throw error;
  }
};

export default skipTransformation;
